import React from 'react'
import {Document, Image, Page, StyleSheet, Text, View} from '@react-pdf/renderer'
import logo from '../../../_websoft/assets/images/logo.jpg'

type Uposlenik = {
  UposlenikImePrezime?: string
}

type TipPanela = {
  Naziv?: string
  naziv?: string
}

export type OtpremnicaPaket = {
  Id: number
  Sirina?: string | number
  Duzina?: string | number
  Komada?: string | number
  DatumIsporuke?: string
  BrojNaloga?: string | number
  BrojFmkNaloga?: string | number
  KupacNaziv?: string
  Komercijalista?: string
  tip_panela?: TipPanela | null
  proizveo_uposlenik?: Uposlenik | null
  isporucio_uposlenik?: Uposlenik | null
}

type Props = {
  paketi: OtpremnicaPaket[]
  datum: string
  ukupnoKvadrata: number
}

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 9,
    color: '#1f2933',
  },
  header: {
    borderBottom: '2px solid #1f2933',
    paddingBottom: 12,
    marginBottom: 18,
  },
  logoTitleWrap: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  logo: {
    width: 250,
    objectFit: 'contain',
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    marginTop: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  subtitle: {
    marginTop: 4,
    fontSize: 9,
    color: '#5f6b7a',
  },
  metaBox: {
    minWidth: 150,
    border: '1px solid #d8dde6',
    padding: 8,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  label: {
    color: '#5f6b7a',
  },
  value: {
    fontWeight: 'bold',
  },
  infoGrid: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  infoCard: {
    flex: 1,
    border: '1px solid #d8dde6',
    padding: 10,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  table: {
    border: '1px solid #1f2933',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#1f2933',
    color: '#ffffff',
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    borderTop: '1px solid #d8dde6',
  },
  cell: {
    padding: 5,
    borderRight: '1px solid #d8dde6',
  },
  numberCell: {
    textAlign: 'right',
  },
  summary: {
    marginTop: 14,
    marginLeft: 'auto',
    width: 210,
    border: '1px solid #1f2933',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 7,
    borderBottom: '1px solid #d8dde6',
  },
  signatureWrap: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 42,
  },
  signature: {
    width: 180,
    borderTop: '1px solid #1f2933',
    paddingTop: 6,
    textAlign: 'center',
  },
})

const getBrojNaloga = (paket: OtpremnicaPaket) => paket.BrojFmkNaloga ?? paket.BrojNaloga ?? '-'

const getTipPanelaLabel = (paket: OtpremnicaPaket) =>
  paket.tip_panela?.Naziv || paket.tip_panela?.naziv || '-'

const parseDimensionValue = (value?: string | number) => {
  if (value === undefined || value === null || value === '') {
    return 0
  }

  return Number(value.toString().replace(',', '.')) || 0
}

const calculateSquareMeters = (paket: OtpremnicaPaket) =>
  (parseDimensionValue(paket.Duzina) / 1000) *
  (parseDimensionValue(paket.Sirina) / 1000) *
  parseDimensionValue(paket.Komada)

const formatSquareMeters = (value: number) => value.toFixed(2)

const OtpremnicaPdf: React.FC<Props> = ({paketi, datum, ukupnoKvadrata}) => {
  const firstPaket = paketi[0]
  const ukupnoKomada = paketi.reduce((sum, paket) => sum + parseDimensionValue(paket.Komada), 0)

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View style={styles.header}>
          <View style={styles.logoTitleWrap}>
            <Image src={logo} style={styles.logo} />
            <Text style={styles.title}>Otpremnice</Text>
            <Text style={styles.subtitle}>Thermoboard paketi</Text>
          </View>
        </View>

        <View style={styles.infoGrid}>
          <View style={styles.infoCard}>
            <Text style={styles.sectionTitle}>Kupac</Text>
            <Text>{firstPaket?.KupacNaziv || '-'}</Text>
          </View>
          <View style={styles.infoCard}>
            <Text style={styles.sectionTitle}>Komercijalista</Text>
            <Text>{firstPaket?.Komercijalista || '-'}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, {width: '13%'}]}>Paket</Text>
            <Text style={[styles.cell, {width: '17%'}]}>Broj naloga</Text>
            <Text style={[styles.cell, {width: '14%'}]}>Vrsta</Text>
            <Text style={[styles.cell, {width: '20%'}]}>Dimenzije</Text>
            <Text style={[styles.cell, styles.numberCell, {width: '10%'}]}>Kom.</Text>
            <Text style={[styles.cell, styles.numberCell, {width: '13%'}]}>m2</Text>
            <Text style={[styles.cell, {width: '13%', borderRight: 0}]}>Isporucio</Text>
          </View>

          {paketi.map((paket) => (
            <View key={paket.Id} style={styles.tableRow}>
              <Text style={[styles.cell, {width: '13%'}]}>{paket.Id}</Text>
              <Text style={[styles.cell, {width: '17%'}]}>{getBrojNaloga(paket)}</Text>
              <Text style={[styles.cell, {width: '14%'}]}>{getTipPanelaLabel(paket)}</Text>
              <Text style={[styles.cell, {width: '20%'}]}>
                {paket.Duzina ?? '-'} x {paket.Sirina ?? '-'}
              </Text>
              <Text style={[styles.cell, styles.numberCell, {width: '10%'}]}>
                {paket.Komada ?? '-'}
              </Text>
              <Text style={[styles.cell, styles.numberCell, {width: '13%'}]}>
                {formatSquareMeters(calculateSquareMeters(paket))}
              </Text>
              <Text style={[styles.cell, {width: '13%', borderRight: 0}]}>
                {paket.isporucio_uposlenik?.UposlenikImePrezime || '-'}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text>Ukupno paketa</Text>
            <Text>{paketi.length}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Ukupno komada</Text>
            <Text>{ukupnoKomada}</Text>
          </View>
          <View style={[styles.summaryRow, {borderBottom: 0}]}>
            <Text>Ukupno m2</Text>
            <Text>{formatSquareMeters(ukupnoKvadrata)}</Text>
          </View>
        </View>

        <View style={styles.signatureWrap}>
          <Text style={styles.signature}>Otpremio</Text>
          <Text style={styles.signature}>Preuzeo</Text>
        </View>
      </Page>
    </Document>
  )
}

export default OtpremnicaPdf
